import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../Services/firebase-config';
import {
    X,
    Upload,
    FileText,
    Link as LinkIcon,
    AlertCircle,
    Calendar,
    BookOpen
} from 'lucide-react';

const ResourceUploadModal = ({ isOpen, onClose, onUpload, selectedDate }) => {
    const [formData, setFormData] = useState({
        contentType: 'event', // 'event' or 'resource'
        title: '',
        description: '',
        type: 'document',
        resourceUrl: '',
        file: null
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({
            ...prev,
            file
        }));
        setError('');
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError('Title is required');
            return false;
        }
        if (!formData.description.trim()) {
            setError('Description is required');
            return false;
        }
        // For resources, validate attachment based on type
        if (formData.contentType === 'resource') {
            if (formData.type === 'link' && !formData.resourceUrl.trim()) {
                setError('Resource URL is required for link type');
                return false;
            }
            if (formData.type === 'document' && !formData.file) {
                setError('Document file is required for document type');
                return false;
            }
        }
        return true;
    };

    const uploadFile = async (file) => {
        const fileName = `resources/${Date.now()}_${file.name}`;
        const fileRef = ref(storage, fileName);
        await uploadBytes(fileRef, file);
        return await getDownloadURL(fileRef);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setUploading(true);
        setError('');

        try {
            let resourceDetails = {};

            if (formData.contentType === 'event') {
                // For events, only add link if provided
                if (formData.resourceUrl.trim()) {
                    resourceDetails.eventUrl = formData.resourceUrl;
                }
            } else {
                // For resources, add file/link based on type
                if (formData.type === 'link' && formData.resourceUrl.trim()) {
                    resourceDetails.resourceUrl = formData.resourceUrl;
                } else if (formData.type === 'document' && formData.file) {
                    const fileUrl = await uploadFile(formData.file);
                    resourceDetails.fileUrl = fileUrl;
                    resourceDetails.fileName = formData.file.name;
                    resourceDetails.fileSize = formData.file.size;
                }
            }

            await onUpload({
                contentType: formData.contentType,
                title: formData.title,
                description: formData.description,
                type: formData.contentType === 'event' ? 'event' : formData.type,
                resourceDetails: Object.keys(resourceDetails).length > 0 ? resourceDetails : null
            });

            // Reset form
            setFormData({
                contentType: 'event',
                title: '',
                description: '',
                type: 'document',
                resourceUrl: '',
                file: null
            });

        } catch (error) {
            console.error('Error creating content:', error);
            setError(`Failed to create ${formData.contentType}. Please try again.`);
        } finally {
            setUploading(false);
        }
    };



    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            Create {formData.contentType === 'event' ? 'Event' : 'Resource'}
                        </h2>
                        <p className="text-gray-600 mt-1">
                            For {selectedDate.toLocaleDateString()}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <p className="text-red-800">{error}</p>
                        </div>
                    )}

                    {/* Content Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            What would you like to create? *
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { value: 'event', label: 'Event', icon: Calendar, description: 'Schedule an event (no attachments)' },
                                { value: 'resource', label: 'Resource', icon: BookOpen, description: 'Upload learning materials with optional attachments' }
                            ].map(({ value, label, icon: Icon, description }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, contentType: value, type: 'document' }))}
                                    className={`p-4 border-2 rounded-lg transition-colors text-left ${formData.contentType === value
                                        ? 'border-custom-blue-1 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <Icon className={`w-6 h-6 mt-1 ${formData.contentType === value ? 'text-custom-blue-1' : 'text-gray-600'
                                            }`} />
                                        <div>
                                            <p className={`font-medium ${formData.contentType === value ? 'text-custom-blue-1' : 'text-gray-900'
                                                }`}>
                                                {label}
                                            </p>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Resource Type - Only show for resources */}
                    {formData.contentType === 'resource' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Attachment Type *
                            </label>
                            <p className="text-sm text-gray-600 mb-3">
                                Choose how you want to attach content to your resource
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { value: 'document', label: 'Document', icon: FileText },
                                    { value: 'link', label: 'Link', icon: LinkIcon }
                                ].map(({ value, label, icon: Icon }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, type: value }))}
                                        className={`p-4 border-2 rounded-lg transition-colors ${formData.type === value
                                            ? 'border-custom-blue-1 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <Icon className={`w-6 h-6 mx-auto mb-2 ${formData.type === value ? 'text-custom-blue-1' : 'text-gray-600'
                                            }`} />
                                        <p className={`text-sm font-medium ${formData.type === value ? 'text-custom-blue-1' : 'text-gray-900'
                                            }`}>
                                            {label}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {formData.contentType === 'event' ? 'Event Title' : 'Resource Title'} *
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                            placeholder={`Enter ${formData.contentType} title`}
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {formData.contentType === 'event' ? 'Event Description' : 'Resource Description'} *
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                            placeholder={`Describe what this ${formData.contentType} covers`}
                            required
                        />
                    </div>

                    {/* Optional Link Field for Events */}
                    {formData.contentType === 'event' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Event Link (Optional)
                            </label>
                            <input
                                type="url"
                                name="resourceUrl"
                                value={formData.resourceUrl}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                placeholder="https://example.com/event-details"
                            />
                            <p className="text-sm text-gray-500 mt-1">
                                Optional link to event details, registration page, or additional information
                            </p>
                        </div>
                    )}

                    {/* Conditional Fields - Only show for resources */}
                    {formData.contentType === 'resource' && (
                        <>
                            {formData.type === 'link' ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Resource URL *
                                    </label>
                                    <input
                                        type="url"
                                        name="resourceUrl"
                                        value={formData.resourceUrl}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-custom-blue-1 focus:border-custom-blue-1"
                                        placeholder="https://example.com/resource"
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Upload Document *
                                    </label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                                        <div className="text-center">
                                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                                            <div className="space-y-2">
                                                <label className="cursor-pointer">
                                                    <span className="text-custom-blue-1 hover:text-blue-700 font-medium">
                                                        Click to upload
                                                    </span>
                                                    <input
                                                        type="file"
                                                        onChange={handleFileChange}
                                                        accept=".pdf,.doc,.docx,.txt"
                                                        className="hidden"
                                                    />
                                                </label>
                                                <p className="text-sm text-gray-500">
                                                    PDF, DOC, DOCX, TXT up to 10MB
                                                </p>
                                            </div>
                                            {formData.file && (
                                                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                                                    <p className="text-sm text-green-800">
                                                        Selected: {formData.file.name}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={uploading}
                            className="flex items-center gap-2 px-6 py-2 bg-custom-blue-1 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Create {formData.contentType === 'event' ? 'Event' : 'Resource'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResourceUploadModal;
